import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Check, Loader2, User, CheckCircle, FileText, X, ArrowRight, ChevronRight, Send, Paperclip, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { tareasDespachoAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import HistorialUsuarios from './HistorialUsuarios';
import { PRIORIDADES, FASES_MINUTAS, TIPOS_COSTAS, ESTADOS_PROPUESTA_INTERESES } from './constants';
import { formatDate, formatDateTime } from './utils';

const MinutaDetailDialog = ({ minuta, isOpen, onClose, onAvanzarFase, usuarios, loadTareas }) => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState('datos'); // datos, email, actuacion
  const [archivos, setArchivos] = useState([]);
  const [loadingArchivos, setLoadingArchivos] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [ultimoNumero, setUltimoNumero] = useState(null);
  const [procuradores, setProcuradores] = useState([]);
  const [selectedProcurador, setSelectedProcurador] = useState(null);
  const [editingFaseA, setEditingFaseA] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [formFaseA, setFormFaseA] = useState({
    descripcion_tarea: '',
    fecha_limite_fase_a: '',
    asignado_a_id: ''
  });
  
  const [formFaseB, setFormFaseB] = useState({
    fecha_presentacion: '',
    estado_propuesta_intereses: '',
    importe_total_minuta: '',
    observaciones_fase_b: '',
    prioridad: 'media',
    asignado_a_id: ''
  });
  const [formFaseC, setFormFaseC] = useState({
    numero_minuta: '',
    tipo_costas: '',
    estado_propuesta_intereses: '',
    importe_minuta_final: '',
    observaciones_fase_c: '',
    asignado_a_id: ''
  });

  // Cargar datos cuando cambia la minuta
  useEffect(() => {
    if (minuta) {
      setFormFaseA({
        descripcion_tarea: minuta.descripcion_tarea || '',
        fecha_limite_fase_a: minuta.fecha_limite_fase_a || '',
        asignado_a_id: minuta.asignado_a_id?.toString() || ''
      });
      setEditingFaseA(false);
      setEditingField(null);
      setFormFaseB({
        fecha_presentacion: minuta.fecha_presentacion || minuta.fecha_limite_fase_a || '',
        estado_propuesta_intereses: minuta.estado_propuesta_intereses || '',
        importe_total_minuta: minuta.importe_total_minuta?.toString() || '',
        observaciones_fase_b: minuta.observaciones_fase_b || '',
        prioridad: minuta.prioridad || 'media',
        asignado_a_id: minuta.asignado_a_id?.toString() || ''
      });
      setFormFaseC({
        numero_minuta: minuta.numero_minuta || '',
        tipo_costas: minuta.tipo_costas || '',
        estado_propuesta_intereses: minuta.estado_propuesta_intereses || '',
        importe_minuta_final: minuta.importe_minuta_final?.toString() || minuta.importe_total_minuta?.toString() || '',
        observaciones_fase_c: minuta.observaciones_fase_c || '',
        asignado_a_id: minuta.asignado_a_id?.toString() || ''
      });
      
      // Cargar archivos
      loadArchivos();
      
      // Cargar procurador si está en fase B o C
      if (minuta.fase_actual !== 'fase_a') {
        loadProcurador();
      }
      
      // Cargar último número si está en fase C
      if (minuta.fase_actual === 'fase_c' && !minuta.numero_minuta) {
        loadUltimoNumero();
      }
      
      // Establecer paso activo
      if (minuta.completada_definitivamente) {
        setActiveStep('actuacion');
      } else if (minuta.email_enviado && minuta.fase_actual === 'fase_c') {
        setActiveStep('actuacion');
      } else if (minuta.fase_actual === 'fase_c' && minuta.numero_minuta) {
        setActiveStep('email');
      } else {
        setActiveStep('datos');
      }
    }
  }, [minuta]);

  const loadArchivos = async () => {
    if (!minuta) return;
    setLoadingArchivos(true);
    try {
      const res = await tareasDespachoAPI.minutas.getArchivos(minuta.id);
      setArchivos(res.data || []);
    } catch (error) {
      console.error('Error cargando archivos:', error);
    } finally {
      setLoadingArchivos(false);
    }
  };

  const loadProcurador = async () => {
    if (!minuta) return;
    try {
      const res = await tareasDespachoAPI.minutas.getProcurador(minuta.id);
      const lista = res.data?.procuradores || [];
      setProcuradores(lista);
      if (minuta.procurador_email) {
        const guardado = lista.find(p => p.email === minuta.procurador_email);
        setSelectedProcurador(guardado || lista[0] || null);
      } else {
        setSelectedProcurador(lista[0] || null);
      }
    } catch (error) {
      console.error('Error cargando procuradores:', error);
    }
  };

  const loadUltimoNumero = async () => {
    if (!minuta) return;
    try {
      const res = await tareasDespachoAPI.minutas.getUltimoNumero(minuta.id);
      setUltimoNumero(res.data);
      if (!formFaseC.numero_minuta && res.data?.sugerido) {
        setFormFaseC(prev => ({ ...prev, numero_minuta: res.data.sugerido }));
      }
    } catch (error) {
      console.error('Error cargando último número:', error);
    }
  };

  const handleFileUpload = async (e, fase) => {
    const files = Array.from(e.target.files);
    if (!files.length || !minuta) return;
    
    setUploadingFile(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of files) {
      try {
        await tareasDespachoAPI.minutas.uploadArchivo(minuta.id, fase, file);
        successCount++;
      } catch (error) {
        console.error(`Error subiendo ${file.name}:`, error);
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast({ title: `${successCount} archivo(s) subido(s) correctamente` });
    }
    if (errorCount > 0) {
      toast({ title: `${errorCount} archivo(s) fallaron`, variant: 'destructive' });
    }
    
    loadArchivos();
    setUploadingFile(false);
    e.target.value = '';
  };

  const handleDeleteArchivo = async (archivoId) => {
    if (!confirm('¿Eliminar este archivo?')) return;
    try {
      await tareasDespachoAPI.minutas.deleteArchivo(minuta.id, archivoId);
      toast({ title: 'Archivo eliminado' });
      loadArchivos();
    } catch (error) {
      toast({ title: 'Error eliminando archivo', variant: 'destructive' });
    }
  };

  const handleGuardarFaseA = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.minutas.updateFaseA(minuta.id, {
        descripcion_tarea: formFaseA.descripcion_tarea,
        fecha_limite_fase_a: formFaseA.fecha_limite_fase_a,
        asignado_a_id: formFaseA.asignado_a_id ? parseInt(formFaseA.asignado_a_id) : null
      });
      toast({ title: 'Fase A actualizada' });
      setEditingFaseA(false);
      loadTareas();
    } catch (error) {
      toast({ title: 'Error actualizando Fase A', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Guardar un campo individual inline (admin)
  const handleSaveInlineField = async (fase, fieldName, value) => {
    setSaving(true);
    try {
      const payload = {};
      if (fieldName === 'asignado_a_id') {
        payload[fieldName] = value ? parseInt(value) : null;
      } else if (fieldName === 'importe_total_minuta') {
        payload[fieldName] = value ? parseFloat(value) : null;
      } else {
        payload[fieldName] = value || null;
      }
      if (fase === 'fase_a') {
        await tareasDespachoAPI.minutas.updateFaseA(minuta.id, payload);
      } else {
        await tareasDespachoAPI.minutas.updateFaseB(minuta.id, payload);
      }
      toast({ title: 'Campo actualizado correctamente' });
      setEditingField(null);
      loadTareas();
    } catch (error) {
      toast({ title: 'Error al guardar', description: error.response?.data?.detail || 'Error desconocido', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleGuardarFaseB = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.minutas.updateFaseB(minuta.id, {
        ...formFaseB,
        importe_total_minuta: formFaseB.importe_total_minuta ? parseFloat(formFaseB.importe_total_minuta) : null
      });
      toast({ title: 'Fase B actualizada' });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleGuardarFaseC = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.minutas.updateFaseC(minuta.id, {
        ...formFaseC,
        importe_minuta_final: formFaseC.importe_minuta_final ? parseFloat(formFaseC.importe_minuta_final) : null
      });
      toast({ title: 'Datos de Fase C guardados' });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!minuta) return;
    
    const emailProcurador = selectedProcurador?.email || minuta.procurador_email;
    if (!emailProcurador) {
      toast({ title: 'No hay email de procurador configurado', variant: 'destructive' });
      return;
    }
    
    // Advertencia si el importe final es diferente del inicial
    const importeInicial = parseFloat(minuta.importe_total_minuta) || 0;
    const importeFinal = parseFloat(formFaseC.importe_minuta_final) || parseFloat(minuta.importe_minuta_final) || 0;
    
    if (importeInicial > 0 && importeFinal > 0 && Math.abs(importeInicial - importeFinal) > 0.01) {
      const diferencia = importeFinal - importeInicial;
      const confirmMsg = `ADVERTENCIA: El importe final (${importeFinal.toFixed(2)}) es diferente del importe inicial (${importeInicial.toFixed(2)}).\n\nDiferencia: ${diferencia.toFixed(2)} (${diferencia > 0 ? '+' : ''}${((diferencia/importeInicial)*100).toFixed(1)}%)\n\nConfirma que los datos son correctos y desea enviar el email?`;
      
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }
    
    setSaving(true);
    try {
      const res = await tareasDespachoAPI.minutas.enviarEmail(minuta.id, {
        procurador_email: selectedProcurador?.email || minuta.procurador_email,
        procurador_nombre: selectedProcurador?.nombre || minuta.procurador_nombre
      });
      toast({ title: 'Email enviado correctamente', description: res.data?.mensaje });
      setActiveStep('actuacion');
      loadTareas();
    } catch (error) {
      toast({ title: 'Error enviando email', description: error.response?.data?.detail || 'Error desconocido', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCrearActuacion = async () => {
    if (!minuta) return;
    
    if (!minuta.numero_minuta && !formFaseC.numero_minuta) {
      toast({ title: 'Debe asignar un número de minuta', variant: 'destructive' });
      return;
    }
    
    // Si hay número pero no está guardado, guardarlo primero
    if (!minuta.numero_minuta && formFaseC.numero_minuta) {
      await handleGuardarFaseC();
    }
    
    setSaving(true);
    try {
      const res = await tareasDespachoAPI.minutas.crearActuacion(minuta.id);
      toast({ title: 'Actuación creada correctamente', description: res.data?.mensaje });
      loadTareas();
      onClose();
    } catch (error) {
      toast({ title: 'Error creando actuación', description: error.response?.data?.detail || 'Error desconocido', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!minuta) return null;

  const faseInfo = FASES_MINUTAS[minuta.fase_actual];
  const isCompleta = minuta.completada_definitivamente;
  const archivosFaseB = archivos.filter(a => a.fase === 'fase_b');
  const archivosFaseC = archivos.filter(a => a.fase === 'fase_c');
  const procuradorInfo = selectedProcurador || (minuta.procurador_nombre ? { nombre: minuta.procurador_nombre, email: minuta.procurador_email } : null);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Detalle de Minuta
            {isCompleta && <Badge className="bg-green-500 text-white ml-2">COMPLETADA - Nº {minuta.numero_minuta}</Badge>}
            {minuta.numero_minuta && !isCompleta && <Badge className="bg-blue-500 text-white ml-2">Nº {minuta.numero_minuta}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
          {Object.entries(FASES_MINUTAS).map(([key, fase], idx) => {
            const isActive = minuta.fase_actual === key;
            const isComplete = 
              (key === 'fase_a' && minuta.fase_a_completada) ||
              (key === 'fase_b' && minuta.fase_b_completada) ||
              (key === 'fase_c' && minuta.completada_definitivamente);
            
            return (
              <React.Fragment key={key}>
                <div className={`flex flex-col items-center ${isActive ? 'scale-110' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                    isComplete ? 'bg-green-500 text-white' : 
                    isActive ? 'bg-blue-500 text-white' : 'bg-gray-200'
                  }`}>
                    {isComplete ? '✓' : fase.icon}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-bold' : ''}`}>
                    {key === 'fase_a' ? 'Solicitud' : key === 'fase_b' ? 'Tramitación' : 'Finalización'}
                  </span>
                </div>
                {idx < 2 && (
                  <ChevronRight className={`w-6 h-6 ${
                    idx === 0 && minuta.fase_a_completada ? 'text-green-500' : 
                    idx === 1 && minuta.fase_b_completada ? 'text-green-500' : 'text-gray-300'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Información general */}
        <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg mb-4">
          <div>
            <Label className="text-gray-500 text-xs">Expediente</Label>
            <p className="font-medium text-sm">{minuta.expediente_numero || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Creado por</Label>
            <p className="font-medium text-sm">{minuta.creado_por_nombre || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Fecha presentación</Label>
            <p className="font-medium text-sm text-blue-600">{formatDate(minuta.fecha_presentacion || minuta.fecha_limite_fase_a)}</p>
          </div>
        </div>

        {/* Procurador info */}
        {procuradores.length > 0 ? (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
            {procuradores.length === 1 ? (
              <div className="flex items-center gap-2 text-purple-800">
                <User className="w-4 h-4" />
                <span className="font-medium">Procurador: {procuradores[0].nombre}</span>
                {procuradores[0].email && (
                  <span className="text-sm">({procuradores[0].email})</span>
                )}
              </div>
            ) : (
              <div className="text-purple-800">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4" />
                  <span className="font-medium">Procuradores ({procuradores.length}):</span>
                </div>
                <div className="ml-6 space-y-0.5">
                  {procuradores.map((p) => (
                    <div key={p.id} className="text-sm">
                      {p.nombre} {p.email ? `(${p.email})` : '(Sin email)'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : procuradorInfo?.nombre ? (
          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-purple-800">
              <User className="w-4 h-4" />
              <span className="font-medium">Procurador: {procuradorInfo.nombre}</span>
              {procuradorInfo.email && (
                <span className="text-sm">({procuradorInfo.email})</span>
              )}
            </div>
          </div>
        ) : null}

        <div className="space-y-4">

          {/* FASE A - Editable si está en fase A y no completada */}
          <Card className={minuta.fase_actual === 'fase_a' ? 'ring-2 ring-yellow-400' : 'opacity-75'}>
            <CardHeader className="py-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2">
                  📝 Fase A - Solicitud
                  {minuta.fase_a_completada && <Badge className="bg-green-500 text-white text-xs">Completada</Badge>}
                </span>
                {minuta.fase_actual === 'fase_a' && !minuta.fase_a_completada && !editingFaseA && (
                  <Button variant="ghost" size="sm" onClick={() => setEditingFaseA(true)}>
                    <Edit className="w-4 h-4 mr-1" /> Editar
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {editingFaseA && minuta.fase_actual === 'fase_a' && !minuta.fase_a_completada ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Descripción</Label>
                    <Input value={formFaseA.descripcion_tarea} onChange={(e) => setFormFaseA({...formFaseA, descripcion_tarea: e.target.value})} placeholder="Descripción..." className="h-8" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Fecha límite</Label>
                      <Input type="date" value={formFaseA.fecha_limite_fase_a} onChange={(e) => setFormFaseA({...formFaseA, fecha_limite_fase_a: e.target.value})} className="h-8" />
                    </div>
                    <div>
                      <Label className="text-xs">Asignado a</Label>
                      <Select value={formFaseA.asignado_a_id} onValueChange={(v) => setFormFaseA({...formFaseA, asignado_a_id: v})}>
                        <SelectTrigger className="h-8"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{usuarios.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingFaseA(false)} disabled={saving}>Cancelar</Button>
                    <Button size="sm" onClick={handleGuardarFaseA} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Guardar</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  {/* Descripción - inline edit */}
                  {editingField === 'descripcion_tarea' ? (
                    <div className="flex items-center gap-2">
                      <Input value={editFieldValue} onChange={(e) => setEditFieldValue(e.target.value)} className="h-7 text-sm flex-1" autoFocus />
                      <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_a', 'descripcion_tarea', editFieldValue)}>
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingField(null)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <p className="flex items-center gap-1">
                      <strong>Descripción:</strong> {minuta.descripcion_tarea || '-'}
                      {isAdmin && minuta.fase_a_completada && (
                        <button data-testid="edit-fase-a-descripcion" className="text-gray-400 hover:text-blue-600 ml-1 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('descripcion_tarea'); setEditFieldValue(minuta.descripcion_tarea || ''); }}>
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </p>
                  )}
                  <div className="flex gap-4 flex-wrap">
                    {/* Fecha límite - inline edit */}
                    {editingField === 'fecha_limite_fase_a' ? (
                      <div className="flex items-center gap-2">
                        <strong className="text-sm whitespace-nowrap">Fecha límite:</strong>
                        <Input type="date" value={editFieldValue} onChange={(e) => setEditFieldValue(e.target.value)} className="h-7 text-sm w-40" autoFocus />
                        <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_a', 'fecha_limite_fase_a', editFieldValue)}>
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingField(null)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1">
                        <strong>Fecha límite:</strong> {formatDate(minuta.fecha_limite_fase_a)}
                        {isAdmin && minuta.fase_a_completada && (
                          <button data-testid="edit-fase-a-fecha" className="text-gray-400 hover:text-blue-600 ml-1 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('fecha_limite_fase_a'); setEditFieldValue(minuta.fecha_limite_fase_a || ''); }}>
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    )}
                    {/* Asignado - inline edit */}
                    {editingField === 'asignado_a_id_fase_a' ? (
                      <div className="flex items-center gap-2">
                        <strong className="text-sm whitespace-nowrap">Asignado:</strong>
                        <Select value={editFieldValue} onValueChange={(v) => setEditFieldValue(v)}>
                          <SelectTrigger className="h-7 w-40 text-sm">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {usuarios.map(u => (
                              <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" className="h-7 px-2 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_a', 'asignado_a_id', editFieldValue)}>
                          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditingField(null)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1">
                        <strong>Asignado:</strong> {minuta.asignado_a_nombre || '-'}
                        {isAdmin && minuta.fase_a_completada && (
                          <button data-testid="edit-fase-a-asignado" className="text-gray-400 hover:text-blue-600 ml-1 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('asignado_a_id_fase_a'); setEditFieldValue(minuta.asignado_a_id?.toString() || ''); }}>
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {minuta.fase_actual === 'fase_a' && !minuta.fase_a_completada && !editingFaseA && (
                <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white mt-2" onClick={() => onAvanzarFase(minuta, 'fase_a')}>
                  Completar Fase A y Avanzar <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* FASE B */}
          {(minuta.fase_a_completada || minuta.fase_actual === 'fase_b') && (
            <Card className={minuta.fase_actual === 'fase_b' ? 'ring-2 ring-blue-400' : ''}>
              <CardHeader className="py-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  📤 Fase B - Tramitación
                  {minuta.fase_b_completada && <Badge className="bg-green-500 text-white text-xs">Completada</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {minuta.fase_actual === 'fase_b' && !minuta.fase_b_completada ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Fecha presentación</Label>
                        <Input 
                          type="date"
                          value={formFaseB.fecha_presentacion}
                          onChange={(e) => setFormFaseB({...formFaseB, fecha_presentacion: e.target.value})}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Importe total (€)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={formFaseB.importe_total_minuta}
                          onChange={(e) => setFormFaseB({...formFaseB, importe_total_minuta: e.target.value})}
                          className="h-8"
                        />
                      </div>
                    </div>

                    {/* Selector de usuario asignado */}
                    <div>
                      <Label className="text-xs">Usuario asignado</Label>
                      <Select 
                        value={formFaseB.asignado_a_id} 
                        onValueChange={(v) => setFormFaseB({...formFaseB, asignado_a_id: v})}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Seleccionar usuario..." />
                        </SelectTrigger>
                        <SelectContent>
                          {usuarios.map(u => (
                            <SelectItem key={u.id} value={u.id.toString()}>
                              {u.nombre || u.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {minuta.asignado_a_nombre && formFaseB.asignado_a_id !== minuta.asignado_a_id?.toString() && (
                        <p className="text-xs text-orange-600 mt-1">
                          Asignado anteriormente: {minuta.asignado_a_nombre}
                        </p>
                      )}
                    </div>
                    
                    {/* Propuesta de intereses */}
                    <div>
                      <Label className="text-xs">Propuesta de Intereses</Label>
                      <Select 
                        value={formFaseB.estado_propuesta_intereses} 
                        onValueChange={(v) => setFormFaseB({...formFaseB, estado_propuesta_intereses: v})}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Seleccionar estado..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADOS_PROPUESTA_INTERESES.map(e => (
                            <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <Select value={formFaseB.prioridad} onValueChange={(v) => setFormFaseB({...formFaseB, prioridad: v})}>
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORIDADES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs">Observaciones</Label>
                      <Textarea 
                        value={formFaseB.observaciones_fase_b}
                        onChange={(e) => setFormFaseB({...formFaseB, observaciones_fase_b: e.target.value})}
                        rows={2}
                        className="text-sm"
                      />
                    </div>

                    {/* Archivos Fase B */}
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-medium">Archivos Fase B</Label>
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" multiple onChange={(e) => handleFileUpload(e, 'fase_b')} />
                          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={uploadingFile} asChild>
                            <span><Upload className="w-3 h-3 mr-1" /> Subir archivo</span>
                          </Button>
                        </label>
                      </div>
                      {archivosFaseB.length > 0 ? (
                        <div className="space-y-1">
                          {archivosFaseB.map(a => (
                            <div key={a.id} className="flex items-center justify-between text-xs bg-white p-2 rounded">
                              <a href={a.ruta} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <Paperclip className="w-3 h-3" /> {a.nombre_original || a.nombre}
                              </a>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteArchivo(a.id)}>
                                <Trash2 className="w-3 h-3 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">Sin archivos</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={handleGuardarFaseB} disabled={saving}>
                        Guardar cambios
                      </Button>
                      <Button 
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                        size="sm"
                        onClick={() => onAvanzarFase(minuta, 'fase_b', formFaseB)}
                      >
                        Completar Fase B <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm space-y-2">
                    <div className="grid grid-cols-4 gap-2">
                      {/* Fecha - inline edit */}
                      <div>
                        {editingField === 'fecha_presentacion' ? (
                          <div className="flex items-center gap-1">
                            <Input type="date" value={editFieldValue} onChange={(e) => setEditFieldValue(e.target.value)} className="h-7 text-xs w-32" autoFocus />
                            <Button size="sm" className="h-6 px-1.5 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_b', 'fecha_presentacion', editFieldValue)}>
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => setEditingField(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1">
                            <strong>Fecha:</strong> {formatDate(minuta.fecha_presentacion)}
                            {isAdmin && minuta.fase_b_completada && (
                              <button data-testid="edit-fase-b-fecha" className="text-gray-400 hover:text-blue-600 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('fecha_presentacion'); setEditFieldValue(minuta.fecha_presentacion || ''); }}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {/* Importe - inline edit */}
                      <div>
                        {editingField === 'importe_total_minuta' ? (
                          <div className="flex items-center gap-1">
                            <Input type="number" step="0.01" value={editFieldValue} onChange={(e) => setEditFieldValue(e.target.value)} className="h-7 text-xs w-24" autoFocus />
                            <Button size="sm" className="h-6 px-1.5 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_b', 'importe_total_minuta', editFieldValue)}>
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => setEditingField(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1">
                            <strong>Importe:</strong> €{minuta.importe_total_minuta?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '-'}
                            {isAdmin && minuta.fase_b_completada && (
                              <button data-testid="edit-fase-b-importe" className="text-gray-400 hover:text-blue-600 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('importe_total_minuta'); setEditFieldValue(minuta.importe_total_minuta?.toString() || ''); }}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {/* Intereses - inline edit */}
                      <div>
                        {editingField === 'estado_propuesta_intereses_b' ? (
                          <div className="flex items-center gap-1">
                            <Select value={editFieldValue} onValueChange={(v) => setEditFieldValue(v)}>
                              <SelectTrigger className="h-7 w-32 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="si">Sí</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" className="h-6 px-1.5 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_b', 'estado_propuesta_intereses', editFieldValue)}>
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => setEditingField(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1">
                            <strong>Intereses:</strong> {minuta.propuesta_intereses_fase_b ? 'Sí' : 'No'}
                            {isAdmin && minuta.fase_b_completada && (
                              <button data-testid="edit-fase-b-intereses" className="text-gray-400 hover:text-blue-600 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('estado_propuesta_intereses_b'); setEditFieldValue(minuta.propuesta_intereses_fase_b ? 'si' : 'no'); }}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {/* Tramitado por - inline edit */}
                      <div>
                        {editingField === 'usuario_fase_b' ? (
                          <div className="flex items-center gap-1">
                            <Select value={editFieldValue} onValueChange={(v) => setEditFieldValue(v)}>
                              <SelectTrigger className="h-7 w-32 text-xs">
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {usuarios.map(u => (
                                  <SelectItem key={u.id} value={u.id.toString()}>{u.nombre || u.email}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="sm" className="h-6 px-1.5 bg-green-500 hover:bg-green-600" disabled={saving} onClick={() => handleSaveInlineField('fase_b', 'asignado_a_id', editFieldValue)}>
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => setEditingField(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1">
                            <strong>Tramitado por:</strong> {minuta.usuario_fase_b_nombre || '-'}
                            {isAdmin && minuta.fase_b_completada && (
                              <button data-testid="edit-fase-b-tramitado" className="text-gray-400 hover:text-blue-600 p-0.5 rounded hover:bg-blue-50 transition-colors" onClick={() => { setEditingField('usuario_fase_b'); setEditFieldValue(minuta.usuario_fase_b_id?.toString() || ''); }}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                    {archivosFaseB.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {archivosFaseB.map(a => (
                          <a key={a.id} href={a.ruta} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                            <Paperclip className="w-3 h-3" /> {a.nombre_original || a.nombre}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* FASE C */}
          {(minuta.fase_b_completada || minuta.fase_actual === 'fase_c') && (
            <Card className={minuta.fase_actual === 'fase_c' && !minuta.completada_definitivamente ? 'ring-2 ring-green-400' : ''}>
              <CardHeader className="py-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  ✅ Fase C - Finalización
                  {minuta.completada_definitivamente && <Badge className="bg-green-500 text-white text-xs">COMPLETADA</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(minuta.fase_actual === 'fase_c' && !minuta.completada_definitivamente) || (isAdmin && minuta.completada_definitivamente) ? (
                  <>
                    {/* Tabs para pasos de Fase C */}
                    <Tabs value={activeStep} onValueChange={setActiveStep} className="w-full">
                      <TabsList className="grid w-full grid-cols-3 h-8">
                        <TabsTrigger value="datos" className="text-xs">1. Datos</TabsTrigger>
                        <TabsTrigger value="email" className="text-xs" disabled={!formFaseC.numero_minuta && !minuta.numero_minuta}>2. Email Procurador</TabsTrigger>
                        <TabsTrigger value="actuacion" className="text-xs" disabled={!minuta.email_enviado}>3. Crear Actuación</TabsTrigger>
                      </TabsList>

                      <TabsContent value="datos" className="space-y-3 mt-3">
                        {/* Sugerencia de número */}
                        {ultimoNumero && (
                          <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <strong>Último número asignado:</strong> {ultimoNumero.ultimo_numero || 'Ninguno'}
                            <br/>
                            <strong>Número sugerido:</strong> <span className="text-green-600 font-bold">{ultimoNumero.sugerido}</span>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Número de Minuta *</Label>
                            <Input 
                              placeholder="Ej: 423/2025-A"
                              value={formFaseC.numero_minuta}
                              onChange={(e) => setFormFaseC({...formFaseC, numero_minuta: e.target.value})}
                              className="h-8 font-mono"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Tipo de Costas *</Label>
                            <Select 
                              value={formFaseC.tipo_costas} 
                              onValueChange={(v) => setFormFaseC({...formFaseC, tipo_costas: v})}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Seleccionar tipo..." />
                              </SelectTrigger>
                              <SelectContent>
                                {TIPOS_COSTAS.map(t => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Importe final (€)</Label>
                            <Input 
                              type="number"
                              step="0.01"
                              value={formFaseC.importe_minuta_final}
                              onChange={(e) => setFormFaseC({...formFaseC, importe_minuta_final: e.target.value})}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Usuario asignado</Label>
                            <Select 
                              value={formFaseC.asignado_a_id} 
                              onValueChange={(v) => setFormFaseC({...formFaseC, asignado_a_id: v})}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Seleccionar usuario..." />
                              </SelectTrigger>
                              <SelectContent>
                                {usuarios.map(u => (
                                  <SelectItem key={u.id} value={u.id.toString()}>
                                    {u.nombre || u.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Propuesta de Intereses</Label>
                          <Select 
                            value={formFaseC.estado_propuesta_intereses} 
                            onValueChange={(v) => setFormFaseC({...formFaseC, estado_propuesta_intereses: v})}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Seleccionar estado..." />
                            </SelectTrigger>
                            <SelectContent>
                              {ESTADOS_PROPUESTA_INTERESES.map(e => (
                                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs">Observaciones</Label>
                          <Textarea 
                            value={formFaseC.observaciones_fase_c}
                            onChange={(e) => setFormFaseC({...formFaseC, observaciones_fase_c: e.target.value})}
                            rows={2}
                            className="text-sm"
                          />
                        </div>

                        {/* Historial de usuarios */}
                        {minuta.historial_usuarios && (
                          <HistorialUsuarios historial={minuta.historial_usuarios} />
                        )}

                        {/* Archivos Fase C */}
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-medium">Archivos Fase C</Label>
                            <label className="cursor-pointer">
                              <input type="file" className="hidden" multiple onChange={(e) => handleFileUpload(e, 'fase_c')} />
                              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={uploadingFile} asChild>
                                <span><Upload className="w-3 h-3 mr-1" /> Subir archivo</span>
                              </Button>
                            </label>
                          </div>
                          {[...archivosFaseB, ...archivosFaseC].length > 0 ? (
                            <div className="space-y-1">
                              {[...archivosFaseB, ...archivosFaseC].map(a => (
                                <div key={a.id} className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                  <a href={a.ruta} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" /> {a.nombre_original || a.nombre}
                                    <Badge variant="outline" className="text-[10px] ml-1">{a.fase}</Badge>
                                  </a>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteArchivo(a.id)}>
                                    <Trash2 className="w-3 h-3 text-red-500" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">Sin archivos</p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={handleGuardarFaseC} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                          </Button>
                          <Button 
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                            size="sm"
                            onClick={() => {
                              if (formFaseC.numero_minuta) {
                                handleGuardarFaseC().then(() => setActiveStep('email'));
                              } else {
                                toast({ title: 'Ingrese el número de minuta', variant: 'destructive' });
                              }
                            }}
                            disabled={!formFaseC.numero_minuta}
                          >
                            Siguiente: Enviar Email <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </TabsContent>

                      <TabsContent value="email" className="space-y-3 mt-3">
                        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <h4 className="font-medium text-sm mb-2">Enviar Email al Procurador</h4>
                          
                          {procuradores.length > 1 && (
                            <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <label className="text-xs font-medium text-yellow-800 mb-1 block">
                                Este expediente tiene {procuradores.length} procuradores. Seleccione a quién enviar:
                              </label>
                              <div className="space-y-1 mt-2">
                                {procuradores.map((proc) => (
                                  <label
                                    key={proc.id}
                                    className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs border transition-colors ${
                                      selectedProcurador?.id === proc.id
                                        ? 'bg-purple-100 border-purple-400'
                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name="procurador_seleccionado"
                                      checked={selectedProcurador?.id === proc.id}
                                      onChange={() => setSelectedProcurador(proc)}
                                      className="accent-purple-500"
                                    />
                                    <span className="font-medium">{proc.nombre}</span>
                                    {proc.email && <span className="text-gray-500">({proc.email})</span>}
                                    {!proc.email && <span className="text-red-500">(Sin email)</span>}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {(selectedProcurador?.email || (procuradores.length === 0 && procuradorInfo?.email)) ? (
                            <>
                              <p className="text-xs text-gray-600 mb-2">
                                Se enviará un email a <strong>{selectedProcurador?.nombre || procuradorInfo?.nombre}</strong> ({selectedProcurador?.email || procuradorInfo?.email}) 
                                solicitando la presentación de la minuta.
                              </p>
                              
                              <div className="bg-white p-2 rounded text-xs">
                                <p><strong>Asunto previsto:</strong></p>
                                <p className="text-gray-600">
                                  REF NUESTRA {minuta.expediente_numero || ''}{minuta.cliente_nombre ? ` Cliente ${minuta.cliente_nombre}` : ''}{minuta.expediente_judicial ? ` Procedimiento ${minuta.expediente_judicial}` : ''}{minuta.juzgado_nombre ? `, Juzgado ${minuta.juzgado_nombre}` : ''}
                                </p>
                              </div>
                              
                              {minuta.email_enviado ? (
                                <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-800">
                                  <CheckCircle className="w-4 h-4 inline mr-1" />
                                  Email enviado el {formatDateTime(minuta.email_fecha_envio)}
                                </div>
                              ) : (
                                <Button 
                                  className="w-full mt-3 bg-purple-500 hover:bg-purple-600 text-white"
                                  size="sm"
                                  onClick={handleEnviarEmail}
                                  disabled={saving || (procuradores.length > 1 && !selectedProcurador?.email)}
                                >
                                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                  Enviar Email a {selectedProcurador?.nombre || procuradorInfo?.nombre || 'Procurador'}
                                </Button>
                              )}
                            </>
                          ) : selectedProcurador && !selectedProcurador.email ? (
                            <div className="text-xs text-red-600">
                              El procurador seleccionado ({selectedProcurador.nombre}) no tiene email configurado. 
                              Seleccione otro procurador o añada un email al contacto.
                            </div>
                          ) : (
                            <div className="text-xs text-red-600">
                              No hay procurador con email asignado a este expediente. 
                              Puede continuar sin enviar email.
                            </div>
                          )}
                        </div>

                        <Button 
                          className="w-full bg-green-500 hover:bg-green-600 text-white"
                          size="sm"
                          onClick={() => setActiveStep('actuacion')}
                        >
                          Siguiente: Crear Actuación <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </TabsContent>

                      <TabsContent value="actuacion" className="space-y-3 mt-3">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <h4 className="font-medium text-sm mb-2">Crear Actuación en Expediente</h4>
                          
                          <div className="text-xs space-y-1 mb-3">
                            <p><strong>Título:</strong> PRESENTAMOS MINUTA {formFaseC.numero_minuta || minuta.numero_minuta} - {minuta.descripcion_tarea}</p>
                            <p><strong>Expediente:</strong> {minuta.expediente_numero}</p>
                            <p><strong>Archivos adjuntos:</strong> {archivos.length}</p>
                            <p><strong>Email procurador:</strong> {minuta.email_enviado ? `Enviado ${formatDateTime(minuta.email_fecha_envio)}` : 'No enviado'}</p>
                          </div>

                          <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 mb-3">
                            Al crear la actuación, la tarea se marcará como <strong>COMPLETADA DEFINITIVAMENTE</strong>.
                          </div>

                          <Button 
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                            onClick={handleCrearActuacion}
                            disabled={saving}
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                            CREAR ACTUACIÓN Y FINALIZAR
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </>
                ) : minuta.completada_definitivamente ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <Label className="text-gray-500 text-xs">Número de minuta</Label>
                          <p className="text-xl font-bold text-green-600">{minuta.numero_minuta}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Importe final</Label>
                          <p className="font-medium">€{minuta.importe_minuta_final?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Realizado por</Label>
                          <p>{minuta.realizado_por_nombre || '-'}</p>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs">Fecha finalización</Label>
                          <p>{formatDateTime(minuta.fecha_completada_definitiva)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {minuta.email_enviado && (
                      <div className="p-2 bg-purple-50 rounded text-xs">
                        <strong>Email enviado:</strong> {formatDateTime(minuta.email_fecha_envio)} a {minuta.procurador_email}
                      </div>
                    )}
                    
                    {minuta.actuacion_creada_id && (
                      <div className="p-2 bg-blue-50 rounded text-xs">
                        <strong>Actuación creada:</strong> ID #{minuta.actuacion_creada_id}
                      </div>
                    )}
                    
                    {/* Historial de usuarios */}
                    {minuta.historial_usuarios && (
                      <HistorialUsuarios historial={minuta.historial_usuarios} />
                    )}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default MinutaDetailDialog;
